import { type BackboneSearchFormModel } from '@fr/vue/types/backboneModels/BackboneSearchFormModel';
import { type BackboneMealsRepository } from '@fr/vue/types/backboneModels/Domain/Repository';
import FactoryProductsAdapt from '@ve/pages/hotel/adapters/FactoryProductsAdapt';
import getStore from '@ve/services/getStore/getStore';
import { type Meal, type CheckInDateRange } from '@ve/types';
import { type IBackboneProduct } from '@ve/types/backboneModels/Domain/ObjectValue';
import processProducts from './processProducts';

const NAMESPACE = 'mapForFilters';

type IStoreHotelPageOptions = {
  tourSearchModel: any;
  searchFormModel: BackboneSearchFormModel;
  currentSearchModel: Backbone.Model<{
    checkInDateRange: CheckInDateRange<Date>;
  }>;
  mealsRepository: BackboneMealsRepository;
};

const getStoreForHotelPageFilters = ({
  tourSearchModel,
  searchFormModel,
  currentSearchModel,
  mealsRepository,
}: IStoreHotelPageOptions) =>
  getStore({
    name: NAMESPACE,
    backboneMap: [
      {
        fields: [
          'selectedTabDate',
          'productType',
          'operators',
          'isExclusive',
          'meals',
          'prepayments',
          'bonusMiles',
        ],
        model: tourSearchModel.get('filterCriteria'),
      },
      {
        fields: [
          {
            meals: {
              field: 'model',
              action: (model, meals) => (meals ? meals.pluck('id') : []),
            },
          },
        ],
        model: searchFormModel.get('meals'),
      },
      {
        fields: [
          {
            warranted: 'model',
          },
        ],
        model: searchFormModel.get('warranted'),
      },
      {
        fields: [
          {
            directFlight: 'model',
          },
        ],
        model: searchFormModel.get('directFlight'),
      },
      {
        fields: [
          {
            block: 'model',
          },
        ],
        model: searchFormModel.get('block'),
      },
      {
        fields: [
          {
            checkInDateRange: {
              field: 'checkInDateRange',
              action: (model, checkInDateRange) => ({
                from: checkInDateRange?.get('from') || null,
                to: checkInDateRange?.get('to') || null,
              }),
            },
          },
        ],
        model: currentSearchModel,
      },
      {
        fields: [
          'loadingState',
          'beforeToursLoading',
          {
            products: {
              field: 'syncTourProductsWithIsExclusive',
              action() {
                const products = tourSearchModel.get(
                  'syncTourProductsWithIsExclusive',
                );
                const rest = processProducts(products, tourSearchModel);
                return processProducts(products, tourSearchModel);
              },
            },
            filteredTourProducts: {
              field: 'filteredTourProductsAll',
              action() {
                const products = tourSearchModel.get('filteredTourProductsAll');
                return processProducts(products, tourSearchModel);
              },
            },
            filteredTourProductsByDates: {
              field: 'filteredTourProducts',
              action() {
                const products = tourSearchModel.get('filteredTourProducts');
                return products.map((product: IBackboneProduct) =>
                  FactoryProductsAdapt(product),
                );
              },
            },
          },
        ],
        model: tourSearchModel,
      },
    ],
    storeOptions: {
      state: {
        mealsRepository: mealsRepository.map((meal: Backbone.Model<Meal>) =>
          meal.pick('id', 'name', 'code'),
        ),
      },
      getters: {
        loadingState: (state) => {
          if (state.beforeToursLoading) {
            return 'beforeToursLoading';
          }
          return state.loadingState;
        },
      },
    },
  });

export default getStoreForHotelPageFilters;

export { NAMESPACE };
